import React, { useEffect, useState } from "react";
import styles from "./Dashboard.module.css";
import CircleIcon from '@mui/icons-material/Circle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CachedIcon from '@mui/icons-material/Cached';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { fetchAuthSession } from 'aws-amplify/auth';


function Dashboard() {
    const [isHidden, setIsHidden] = useState(true);
    const [eye, setEye] = useState(false); // For visibility
    const [nameCopied, setNameCopied] = useState(false);
    const [valueCopied, setValueCopied] = useState(false);
    const [tokeCopied, setTokenCopied] = useState(false);

    // Handle data storage
    const [token, setToken] = useState("Fetching...");
    const [creditUsage, setCreditUsage] = useState("Fetching...");
    const [userTier, setUserTier] = useState("Fetching...");
    const [nextRenewal, setNextRenewal] = useState("Fetching...");
    const [projectTier, setProjectTier] = useState("Fetching...");

    // Fetch user details
    useEffect(() => {
        fetchUserDetails();
    }, []); 

    async function fetchUserDetails() {
        try {
          // Get JWT token
          const session = await fetchAuthSession();
          const jwt = session.tokens.accessToken;
    
          const headers = { "Authorization": `Bearer ${jwt}` };
    
          // Fetch data concurrently
          await Promise.all([
            fetch(
              `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_API_KEY_ENDPOINT}`,
              { headers }
            ).then((res) => res.json())
            .then((data) => {
                setToken(data.response.resultMessage);
            }),
            fetch(
              `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_PLAN_USAGE_ENDPOINT}`,
              {
                method: "POST",
                headers,
                body: JSON.stringify({
                  project_name: process.env.REACT_APP_PROJECT_NAME,
                }),
              }
            ).then((res) => res.json())
            .then((data) => {
                const resultMessage = data.response.resultMessage;

                setCreditUsage(
                    `${Math.round(resultMessage.credits_remaining)}/${Math.round(resultMessage.tier_information.credits)}`
                );
                  
                // Capitalizing first character
                setUserTier(
                    resultMessage.tier_information.tier_name.charAt(0).toUpperCase() +
                    resultMessage.tier_information.tier_name.slice(1)
                );
                  
                setNextRenewal(resultMessage.next_renewal);

                setProjectTier(resultMessage.tier);

                console.log("result: ", resultMessage)
            }),
          ]);
        } catch (error) {
          console.error("Error fetching user details:", error);
        }
    };

    async function regenAPIKey() {
        try {
          // Get JWT token
          const session = await fetchAuthSession();
          const jwt = session.tokens.accessToken;
      
          const headers = { "Authorization": `Bearer ${jwt}` };
      
          const response = await fetch(
            `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_REGEN_API_KEY_ENDPOINT}`,
            { method: "POST", headers }
          );
      
          const data = await response.json();
      
          if(response.ok) {
            console.log("regenerated token: ", data.response.resultMessage);
            setToken(data.response.resultMessage);
          } else {
            console.error("Error regenerating API key:", data);
          }
        } catch (error) {
          console.error("Error regenerating API key:", error);
        }
    }

    function toggleVisibility() {
        setIsHidden(!isHidden);
    }

    function eyeClick() {
        setEye(!eye);
        toggleVisibility();
    }

    function copyContent(field, content) {
        // Check if the Clipboard API is supported
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(content)
                .then(() => {
                    console.log("Content copied to clipboard!");

                    // Show copied indicator
                    if (field === "name") {
                        setNameCopied(true);
                        setTimeout(() => setNameCopied(false), 1000); // Reset after 1 second
                    } else if (field === "value") {
                        setValueCopied(true);
                        setTimeout(() => setValueCopied(false), 1000); // Reset after 1 second
                    } else if (field === "token") {
                        setTokenCopied(true);
                        setTimeout(() => setTokenCopied(false), 1000); // Reset after 1 second
                    }
                })
                .catch((err) => {
                    console.error("Failed to copy content: ", err);
                });
        } else {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = content;
            textArea.style.position = "fixed"; // Avoid scrolling to the end of the page
            textArea.style.top = "0";
            textArea.style.left = "0";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
                document.execCommand("copy");
                console.log("Content copied to clipboard!");

                // Show copied indicator
                if (field === "name") {
                    setNameCopied(true);
                    setTimeout(() => setNameCopied(false), 4000); 
                } else if (field === "value") {
                    setValueCopied(true);
                    setTimeout(() => setValueCopied(false), 4000);
                } else if (field === "token") {
                    setTokenCopied(true);
                    setTimeout(() => setTokenCopied(false), 4000); // Reset after 1 second
                }
            } catch (err) {
                console.error("Fallback: Could not copy content", err);
            }
            
            document.body.removeChild(textArea);
        }
    }
    
    return (
        <div className={styles["dashboard-container"]}>
            <div className={styles["dashboard-content"]}>
                <div className={styles["dashboard-header"]}>
                    <h6>Your Account</h6>

                    <p><span className={styles["bold-txt"]}>Account Tier:</span> Free</p>
                </div>

                <div className={styles["active-tokens"]}>
                    <div className={styles["active-tokens-header"]}>
                        <div className={styles["name-tablet"]}>
                            <p>Active API Key</p>

                            <CircleIcon className={styles["circle-icon"]} />
                        </div>

                        <p className={styles["credits-used"]}>
                            <span className={styles["bold-txt"]}>Credit Usage:</span> {creditUsage}
                        </p>
                    </div>

                    <div className={styles["active-tokens-content"]}>
                        <div className={styles["api-token-container"]}>
                            <p>
                                <span className={styles["bold-txt"]}>API Key: </span> 
                                {isHidden ? "*".repeat(token.length) : token}
                            </p>

                            <div className={styles["dashboard-icon-container"]}>
                                {eye?
                                    <VisibilityIcon 
                                        className={styles["viz-icon"]} 
                                        onClick={eyeClick}
                                    />
                                    :
                                    <VisibilityOffIcon 
                                        className={styles["viz-icon"]} 
                                        onClick={eyeClick}
                                    />
                                }
                            </div>

                            <div className={styles["dashboard-icon-container"]}>
                                <CachedIcon 
                                    className={styles["refresh-icon"]} 
                                    onClick={regenAPIKey}
                                />
                            </div>

                            <div className={styles["dashboard-icon-container"]}>
                                <ContentCopyIcon 
                                    className={styles["copy-icon"]} 
                                    onClick={() => copyContent("token", token)}
                                />
                                <div className={tokeCopied ? `${styles["copied-alert"]} ${styles["show-alert"]}` : styles["copied-alert"]}>
                                    Copied
                                </div>
                            </div>
                            
                        </div>

                        <p>
                            <span className={styles["bold-txt"]}>Key Tier:</span> {userTier}
                        </p>

                        <p>
                            <span className={styles["bold-txt"]}>Next Renewal:</span> {nextRenewal}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Dashboard;